import { useEffect, useMemo, useState } from 'react';

import type { CreateLicensePlanRequest, LicensePlanDefinitionResponse, UpdateLicensePlanRequest } from '../api';
import { useOcrProviders } from '../hooks/useOcrProviders';
import { usePlatformProviders } from '../hooks/usePlatformProviders';

interface EditPlanModalProps {
  /** Non-null = edit mode. Null + createMode = create mode. */
  plan: LicensePlanDefinitionResponse | null;
  createMode?: boolean;
  busy: boolean;
  onClose: () => void;
  onSubmit: (licenseType: string, request: UpdateLicensePlanRequest) => void;
  onCreate?: (request: CreateLicensePlanRequest) => void;
}

const SLUG_RE = /^[a-z0-9-]+$/;

// Numeric inputs are kept as strings so users can blank a field to mean "unlimited" (null).
interface FormState {
  /** Only used in create mode — the new plan id. */
  newLicenseType: string;
  displayName: string;
  hasCost: boolean;
  monthlyCostAmount: string;
  costCurrency: string;
  documentsPerMonth: string;
  maxDocumentTypes: string;
  maxUsers: string;
  jobRetentionDays: string;
  diRequestsPerMonth: string;
  diPagesPerMonth: string;
  allowsLocalAiProviders: boolean;
  allowsWebhooks: boolean;
  // Key present = provider is enabled for this plan. Value is the monthly-call-limit string ('' = unlimited).
  providerLimits: Record<string, string>;
  // OCR cost-control caps ('' = unlimited / null)
  readCallsPerMonth: string;
  layoutCallsPerMonth: string;
  documentCallsPerMonth: string;
  // Auto OCR engine routing by complexity (opt-in; off keeps the legacy "null provider = DI" behaviour).
  autoOcrRoutingEnabled: boolean;
  complexDocOcrProviderId: string;
  // Auto-classification of untyped uploads (opt-in; off keeps the "type required at upload" behaviour).
  autoClassificationEnabled: boolean;
}

function nToStr(value: number | null | undefined): string {
  return value === null || value === undefined ? '' : String(value);
}

function strToN(value: string): number | null {
  if (value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function planToForm(plan: LicensePlanDefinitionResponse): FormState {
  return {
    newLicenseType: '',
    displayName: plan.displayName,
    hasCost: plan.hasCost,
    monthlyCostAmount: nToStr(plan.monthlyCostAmount),
    costCurrency: plan.costCurrency,
    documentsPerMonth: nToStr(plan.documentsPerMonth),
    maxDocumentTypes: nToStr(plan.maxDocumentTypes),
    maxUsers: nToStr(plan.maxUsers),
    jobRetentionDays: String(plan.jobRetentionDays),
    diRequestsPerMonth: nToStr(plan.diRequestsPerMonth),
    diPagesPerMonth: nToStr(plan.diPagesPerMonth),
    allowsLocalAiProviders: plan.allowsLocalAiProviders,
    allowsWebhooks: plan.allowsWebhooks,
    providerLimits: Object.fromEntries(
      plan.allowedPlatformProviders.map((p) => [p.platformProviderId, nToStr(p.monthlyCallLimit)]),
    ),
    readCallsPerMonth: nToStr(plan.readCallsPerMonth),
    layoutCallsPerMonth: nToStr(plan.layoutCallsPerMonth),
    documentCallsPerMonth: nToStr(plan.documentCallsPerMonth),
    autoOcrRoutingEnabled: plan.autoOcrRoutingEnabled,
    complexDocOcrProviderId: plan.complexDocOcrProviderId ?? '',
    autoClassificationEnabled: plan.autoClassificationEnabled,
  };
}

const EMPTY_FORM: FormState = {
  newLicenseType: '',
  displayName: '',
  hasCost: false,
  monthlyCostAmount: '',
  costCurrency: 'USD',
  documentsPerMonth: '',
  maxDocumentTypes: '',
  maxUsers: '',
  jobRetentionDays: '30',
  diRequestsPerMonth: '',
  diPagesPerMonth: '',
  allowsLocalAiProviders: false,
  allowsWebhooks: false,
  providerLimits: {},
  readCallsPerMonth: '',
  layoutCallsPerMonth: '',
  documentCallsPerMonth: '',
  autoOcrRoutingEnabled: false,
  complexDocOcrProviderId: '',
  autoClassificationEnabled: false,
};

export function EditPlanModal({ plan, createMode = false, busy, onClose, onSubmit, onCreate }: EditPlanModalProps): JSX.Element | null {
  const [form, setForm] = useState<FormState | null>(null);
  const { data: providers } = usePlatformProviders();
  const { data: ocrProviders } = useOcrProviders();

  useEffect(() => {
    if (plan !== null) {
      setForm(planToForm(plan));
    } else if (createMode) {
      setForm({ ...EMPTY_FORM });
    } else {
      setForm(null);
    }
  }, [plan, createMode]);

  // Union of live providers and any already-assigned ids whose provider may have been deleted.
  // Preserves legacy references so admins can deselect them without data loss.
  const providerRows = useMemo(() => {
    const live = providers ?? [];
    const assignedIds = new Set(plan?.allowedPlatformProviders.map((p) => p.platformProviderId) ?? []);
    const liveIds = new Set(live.map((p) => p.id));
    const orphans = (plan?.allowedPlatformProviders ?? [])
      .filter((p) => !liveIds.has(p.platformProviderId))
      .map((p) => ({ id: p.platformProviderId, displayName: p.displayName ?? p.platformProviderId, modelName: '', isActive: false, orphan: true as const }));
    const liveRows = live.map((p) => ({ id: p.id, displayName: p.displayName, modelName: p.modelName, isActive: p.isActive, orphan: false as const }));
    return [...liveRows, ...orphans].sort((a, b) => {
      const aSel = assignedIds.has(a.id) ? 0 : 1;
      const bSel = assignedIds.has(b.id) ? 0 : 1;
      if (aSel !== bSel) return aSel - bSel;
      return a.displayName.localeCompare(b.displayName);
    });
  }, [providers, plan]);

  if (form === null) return null;
  if (!createMode && plan === null) return null;

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (form.displayName.trim() === '' || form.costCurrency.trim() === '') return;

    const sharedFields = {
      displayName: form.displayName.trim(),
      hasCost: form.hasCost,
      monthlyCostAmount: form.hasCost ? strToN(form.monthlyCostAmount) : null,
      costCurrency: form.costCurrency.trim(),
      documentsPerMonth: strToN(form.documentsPerMonth),
      maxDocumentTypes: strToN(form.maxDocumentTypes),
      maxUsers: strToN(form.maxUsers),
      jobRetentionDays: strToN(form.jobRetentionDays) ?? 0,
      diRequestsPerMonth: strToN(form.diRequestsPerMonth),
      diPagesPerMonth: strToN(form.diPagesPerMonth),
      allowsLocalAiProviders: form.allowsLocalAiProviders,
      allowsWebhooks: form.allowsWebhooks,
      allowedPlatformProviders: Object.entries(form.providerLimits).map(([id, limit]) => ({
        platformProviderId: id,
        monthlyCallLimit: strToN(limit),
      })),
      readCallsPerMonth: strToN(form.readCallsPerMonth),
      layoutCallsPerMonth: strToN(form.layoutCallsPerMonth),
      documentCallsPerMonth: strToN(form.documentCallsPerMonth),
      autoOcrRoutingEnabled: form.autoOcrRoutingEnabled,
      complexDocOcrProviderId:
        form.autoOcrRoutingEnabled && form.complexDocOcrProviderId.trim() !== ''
          ? form.complexDocOcrProviderId
          : null,
      autoClassificationEnabled: form.autoClassificationEnabled,
    };

    if (createMode) {
      const slug = form.newLicenseType.trim();
      if (!SLUG_RE.test(slug)) return;
      onCreate?.({ licenseType: slug, ...sharedFields });
    } else if (plan !== null) {
      onSubmit(plan.licenseType, sharedFields);
    }
  };

  const text = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => prev !== null ? { ...prev, [key]: e.target.value } : null);
  };
  const bool = (key: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => prev !== null ? { ...prev, [key]: e.target.checked } : null);
  };

  const toggleProvider = (id: string, checked: boolean): void => {
    setForm((prev) => {
      if (prev === null) return null;
      const next = { ...prev.providerLimits };
      if (checked) next[id] = next[id] ?? '';
      else delete next[id];
      return { ...prev, providerLimits: next };
    });
  };

  const setProviderLimit = (id: string, value: string): void => {
    setForm((prev) => {
      if (prev === null) return null;
      return { ...prev, providerLimits: { ...prev.providerLimits, [id]: value } };
    });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-plan-title"
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-lg overflow-y-auto rounded-2xl border border-slate-200/70 bg-white p-4 shadow-xl sm:p-6"
        style={{ maxHeight: '90vh' }}
      >
        <h2 id="edit-plan-title" className="text-lg font-semibold text-slate-900">
          {createMode ? 'Nuevo plan' : `Editar plan · ${plan?.licenseType}`}
        </h2>

        <div className="mt-5 space-y-4">
          {createMode ? (
            <div>
              <label className="label" htmlFor="ep-slug">ID del plan (slug)</label>
              <input
                id="ep-slug"
                className="input font-mono"
                placeholder="mi-plan-custom"
                value={form.newLicenseType}
                onChange={text('newLicenseType')}
                disabled={busy}
                required
                pattern="[a-z0-9-]+"
                title="Solo letras minúsculas, números y guiones"
              />
              <p className="mt-1 text-xs text-slate-500">Solo letras minúsculas, números y guiones. No se puede cambiar después.</p>
            </div>
          ) : (
            <div>
              <p className="label">ID del plan</p>
              <p className="input bg-slate-50 font-mono text-slate-500">{plan?.licenseType}</p>
            </div>
          )}

          <div>
            <label className="label" htmlFor="ep-name">Nombre visible</label>
            <input
              id="ep-name"
              className="input"
              value={form.displayName}
              onChange={text('displayName')}
              disabled={busy}
              required
              maxLength={80}
            />
          </div>

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={form.hasCost}
              onChange={bool('hasCost')}
              disabled={busy}
              className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
            />
            <span className="text-sm font-medium text-slate-900">Plan de pago</span>
          </label>

          {form.hasCost ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="label" htmlFor="ep-cost">Importe mensual</label>
                <input
                  id="ep-cost"
                  type="number"
                  min={0}
                  step={0.01}
                  className="input"
                  value={form.monthlyCostAmount}
                  onChange={text('monthlyCostAmount')}
                  disabled={busy}
                />
              </div>
              <div>
                <label className="label" htmlFor="ep-currency">Moneda</label>
                <input
                  id="ep-currency"
                  className="input"
                  value={form.costCurrency}
                  onChange={text('costCurrency')}
                  disabled={busy}
                  maxLength={3}
                  required
                />
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="label" htmlFor="ep-docs">Docs/mes (vacío = ∞)</label>
              <input id="ep-docs" type="number" min={0} className="input" value={form.documentsPerMonth} onChange={text('documentsPerMonth')} disabled={busy} />
            </div>
            <div>
              <label className="label" htmlFor="ep-types">Tipos doc. máx. (vacío = ∞)</label>
              <input id="ep-types" type="number" min={1} className="input" value={form.maxDocumentTypes} onChange={text('maxDocumentTypes')} disabled={busy} />
            </div>
            <div>
              <label className="label" htmlFor="ep-users">Usuarios máx. (vacío = ∞)</label>
              <input id="ep-users" type="number" min={1} className="input" value={form.maxUsers} onChange={text('maxUsers')} disabled={busy} />
            </div>
            <div>
              <label className="label" htmlFor="ep-retention">Retención (días)</label>
              <input id="ep-retention" type="number" min={1} className="input" value={form.jobRetentionDays} onChange={text('jobRetentionDays')} disabled={busy} required />
            </div>
            <div>
              <label className="label" htmlFor="ep-direq">DI requests/mes (vacío = ∞)</label>
              <input id="ep-direq" type="number" min={0} className="input" value={form.diRequestsPerMonth} onChange={text('diRequestsPerMonth')} disabled={busy} />
            </div>
            <div>
              <label className="label" htmlFor="ep-dipages">DI páginas/mes (vacío = ∞)</label>
              <input id="ep-dipages" type="number" min={0} className="input" value={form.diPagesPerMonth} onChange={text('diPagesPerMonth')} disabled={busy} />
            </div>
          </div>

          <div>
            <div className="flex items-baseline justify-between">
              <h3 className="text-sm font-semibold text-slate-900">OCR (control de coste)</h3>
              <span className="text-xs text-slate-500">Vacío = ilimitado</span>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-3">
              <div>
                <label className="label" htmlFor="ep-ocr-read">Llamadas prebuilt-read / mes</label>
                <input id="ep-ocr-read" type="number" min={0} className="input" value={form.readCallsPerMonth} onChange={text('readCallsPerMonth')} disabled={busy} />
              </div>
              <div>
                <label className="label" htmlFor="ep-ocr-layout">prebuilt-layout / mes</label>
                <input id="ep-ocr-layout" type="number" min={0} className="input" value={form.layoutCallsPerMonth} onChange={text('layoutCallsPerMonth')} disabled={busy} />
              </div>
              <div>
                <label className="label" htmlFor="ep-ocr-document">prebuilt-document / mes</label>
                <input id="ep-ocr-document" type="number" min={0} className="input" value={form.documentCallsPerMonth} onChange={text('documentCallsPerMonth')} disabled={busy} />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-900">OCR automático por complejidad</h3>
            <p className="mt-1 text-xs text-slate-500">
              Para los tipos de documento que no fijen un proveedor concreto: los documentos simples (solo
              texto) usan Azure Document Intelligence (más barato) y los complejos (tablas/formularios) usan
              el proveedor elegido, con vuelta automática a Document Intelligence si falla. Apagado = todo va
              a Document Intelligence (comportamiento actual).
            </p>
            <label className="mt-3 flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.autoOcrRoutingEnabled}
                onChange={bool('autoOcrRoutingEnabled')}
                disabled={busy}
                className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
              />
              Activar enrutado automático del motor OCR
            </label>
            {form.autoOcrRoutingEnabled ? (
              <div className="mt-3">
                <label className="label" htmlFor="ep-complex-provider">Proveedor para documentos complejos</label>
                <select
                  id="ep-complex-provider"
                  className="input"
                  value={form.complexDocOcrProviderId}
                  onChange={(e) => setForm((prev) => (prev !== null ? { ...prev, complexDocOcrProviderId: e.target.value } : null))}
                  disabled={busy}
                >
                  <option value="">Ninguno — todo en Document Intelligence</option>
                  {(ocrProviders ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.displayName}{p.modelId !== null ? ` — ${p.modelId}` : ''}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-slate-500">
                  Los tipos con tablas o pares clave/valor se enrutarán a este proveedor (p. ej. Mistral OCR 4).
                </p>
              </div>
            ) : null}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-slate-900">Clasificación automática</h3>
            <p className="mt-1 text-xs text-slate-500">
              Permite a los tenants de este plan subir documentos en modo «Automático»: el sistema
              detecta el tipo entre sus tipos publicados antes de extraer. Apagado = hay que elegir el
              tipo en cada subida (comportamiento actual).
            </p>
            <label className="mt-3 flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.autoClassificationEnabled}
                onChange={bool('autoClassificationEnabled')}
                disabled={busy}
                className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
              />
              Activar clasificación automática
            </label>
          </div>

          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.allowsLocalAiProviders} onChange={bool('allowsLocalAiProviders')} disabled={busy} className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary" />
              Providers locales (Ollama)
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.allowsWebhooks} onChange={bool('allowsWebhooks')} disabled={busy} className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary" />
              Webhooks
            </label>
          </div>

          <div>
            <div className="flex items-baseline justify-between">
              <h3 className="text-sm font-semibold text-slate-900">Platform providers disponibles</h3>
              <span className="text-xs text-slate-500">Vacío = ∞ llamadas/mes</span>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Modelos que verán por defecto los tenants con este plan.
            </p>
            {providerRows.length === 0 ? (
              <p className="mt-3 text-sm text-slate-500">
                No hay platform providers configurados. Créalos primero en la pestaña Providers.
              </p>
            ) : (
              <ul className="mt-3 space-y-2">
                {providerRows.map((row) => {
                  const selected = Object.prototype.hasOwnProperty.call(form.providerLimits, row.id);
                  return (
                    <li
                      key={row.id}
                      className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2"
                    >
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={(e) => toggleProvider(row.id, e.target.checked)}
                        disabled={busy}
                        className="h-4 w-4 rounded border-slate-300 text-primary focus:ring-primary"
                        aria-label={`Asignar ${row.displayName} a este plan`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-slate-900">{row.displayName}</span>
                          {row.orphan ? (
                            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">eliminado</span>
                          ) : !row.isActive ? (
                            <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-600">inactivo</span>
                          ) : null}
                        </div>
                        {row.modelName !== '' ? (
                          <span className="block truncate text-xs text-slate-500">{row.modelName}</span>
                        ) : null}
                      </div>
                      <input
                        type="number"
                        min={0}
                        placeholder="∞"
                        value={selected ? form.providerLimits[row.id] : ''}
                        onChange={(e) => setProviderLimit(row.id, e.target.value)}
                        disabled={busy || !selected}
                        className="input w-28 text-right text-sm"
                        aria-label={`Llamadas/mes para ${row.displayName}`}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>Cancelar</button>
          <button type="submit" className="btn-primary" disabled={busy}>{busy ? 'Guardando…' : createMode ? 'Crear plan' : 'Guardar'}</button>
        </div>
      </form>
    </div>
  );
}